import {
  InputHTMLAttributes,
  ReactElement,
  ReactNode,
  forwardRef,
  memo,
} from 'react'

import FormError from '@/components/FormError'
import { cn } from '@/lib/misc/Utils'
import LoadingSpinner from '@/components/LoadingSpinner'
import { GenericObject } from '@/lib/definitions/generic.types'
import useParseFormErrors from '@/lib/hooks/useParseFormErrors'

const boxClasses = {
  base: 'h-full w-full px-3 border bg-surface rounded-lg border-solid border-neutral-30 transition-shadow peer-focus:border-accent-60 peer-focus:ring-2 peer-focus:ring-accent-60/25 peer-hover:border-neutral-40 peer-disabled:bg-neutral-10 peer-disabled:border-hairline peer-disabled:pointer-events-none',
  error:
    'border-status-error! peer-focus:border-status-error peer-focus:ring-status-error/20 peer-hover:border-status-error',
} as const

const inputClasses = {
  base: 'peer appearance-none text-neutral-100 text-[14px] focus:outline-hidden absolute bottom-2 left-3 right-3 w-[-moz-available] placeholder:text-neutral-60',
  center: '-translate-y-2/4 top-2/4 bottom-auto!',
} as const

export interface BasicInputProps {
  className?: string
  cy?: string
  label?: ReactNode
  errors?: (string | undefined | GenericObject)[]
  icon?: ReactElement
}

export interface InputProps
  extends BasicInputProps, InputHTMLAttributes<HTMLInputElement> {
  classicLabel?: string
  children?: ReactNode
  loading?: boolean
  isSmall?: boolean
  heightClassName?: string
  suffix?: ReactNode
  inputPrefix?: ReactNode
  prefixWidth?: string
  inputClassName?: string
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      classicLabel,
      cy,
      errors,
      children,
      loading,
      type = 'text',
      name,
      icon,
      isSmall,
      heightClassName,
      suffix,
      inputPrefix,
      prefixWidth = '32px',
      inputClassName,
      ...props
    },
    ref
  ) => {
    const { parsedErrors } = useParseFormErrors(errors, name)

    return (
      <div className={cn(className, 'relative')}>
        {!!classicLabel && (
          <div className="text-[13px] font-medium text-neutral-60 mb-1.5">
            {classicLabel}
          </div>
        )}
        {!!icon && (
          <div className="absolute -translate-y-2/4 pl-2 top-2/4 z-20">
            {icon}
          </div>
        )}
        {!!suffix && (
          <div className="absolute -translate-y-2/4 right-3 top-2/4 z-20">
            {suffix}
          </div>
        )}
        {!!inputPrefix && (
          <div
            className="absolute -translate-y-2/4 left-3 top-[29px] z-20"
            style={{ width: prefixWidth }}
          >
            {inputPrefix}
          </div>
        )}
        <label
          className={cn(
            'relative flex justify-center items-center',
            heightClassName ?? (isSmall ? 'h-[40px]' : 'h-[50px]')
          )}
        >
          <input
            data-cy={cy ? cy : !!name && `input-${name}`}
            className={cn(
              'inter-input-base',
              inputClasses.base,
              !!icon && 'pl-6',
              (type === 'search' || !!classicLabel || !label) &&
                inputClasses.center,
              !!props.value && !!label && '-translate-y-[1px]',
              !!inputPrefix && 'pl-6',
              !!inputClassName && inputClassName
            )}
            placeholder={props.placeholder || ' '}
            ref={ref}
            type={type}
            name={name}
            style={{ paddingLeft: !!inputPrefix ? prefixWidth : undefined }}
            {...props}
          />
          <div
            className={cn(
              boxClasses.base,
              !!parsedErrors.length && boxClasses.error
            )}
          />
          {!!label && (
            <span
              className={cn(
                'absolute duration-200 text-neutral-50 text-[11px] left-3 -translate-y-2/4 top-3 peer-placeholder-shown:text-[14px] peer-placeholder-shown:top-2/4 peer-focus:top-3 peer-focus:text-[11px] peer-autofill:text-[11px] peer-autofill:top-3 overflow-hidden whitespace-nowrap',
                !!props.placeholder && 'text-[11px]! top-3!'
              )}
            >
              {label}
              {!!props.required &&
                typeof label === 'string' &&
                !label.includes('*') &&
                '*'}
            </span>
          )}
          {children}
          {loading && (
            <div className="absolute -translate-y-2/4 right-3 top-2/4 z-20">
              <LoadingSpinner className="h-5 w-5 text-accent-100" />
            </div>
          )}
        </label>
        {parsedErrors.map(
          (error, index) =>
            !!error && (
              <FormError
                cy={
                  cy
                    ? `form-errors-${cy}`
                    : !!name
                      ? `form-errors-${name}`
                      : undefined
                }
                key={index}
              >
                {error}
              </FormError>
            )
        )}
      </div>
    )
  }
)

export default memo(Input)
